'use client';

import { useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface DestinationRFQModalProps {
  forwarder: {
    id: string;
    company_name: string;
  };
  blReference: string;
  destinationCountry: string;
  destinationPort?: string;
  isOpen: boolean;
  onClose: () => void;
}

type SubmitState = 'idle' | 'submitting' | 'success_confirmed' | 'success_pending' | 'error';

export function DestinationRFQModal({
  forwarder,
  blReference,
  destinationCountry,
  destinationPort,
  isOpen,
  onClose,
}: DestinationRFQModalProps) {
  const [customsClearance, setCustomsClearance] = useState(true);
  const [localDelivery, setLocalDelivery] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const autoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check auth on open
  useEffect(() => {
    if (!isOpen) return;
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user?.email) {
        setUserEmail(user.email);
        setEmail(user.email);
      } else {
        setUserEmail(null);
      }
    });
  }, [isOpen]);

  // Auto-close after success
  useEffect(() => {
    if (submitState === 'success_confirmed' || submitState === 'success_pending') {
      autoCloseRef.current = setTimeout(() => {
        handleClose();
      }, 4000);
    }
    return () => {
      if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitState]);

  function handleClose() {
    if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    setSubmitState('idle');
    setErrorMsg('');
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const services: string[] = [];
    if (customsClearance) services.push('customs_clearance');
    if (localDelivery) services.push('local_delivery');
    if (services.length === 0) {
      setErrorMsg('Please select at least one service.');
      return;
    }

    setSubmitState('submitting');
    setErrorMsg('');

    try {
      const res = await fetch('/api/destination-rfq', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          forwarder_id: forwarder.id,
          bl_reference: blReference,
          destination_country: destinationCountry,
          destination_port: destinationPort ?? undefined,
          services_requested: services,
          delivery_address: localDelivery && deliveryAddress.trim() ? deliveryAddress.trim() : undefined,
          requester_email: email.trim(),
          requester_name: name.trim() || undefined,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setErrorMsg(data.error ?? 'Something went wrong. Please try again.');
        setSubmitState('error');
        return;
      }

      if (data.email_confirmed) {
        setSubmitState('success_confirmed');
      } else {
        setSubmitState('success_pending');
      }
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setSubmitState('error');
    }
  }

  if (!isOpen) return null;

  return (
    <div className="drfq-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="drfq-modal" role="dialog" aria-modal="true" aria-label="Request customs and delivery services">
        {/* Header */}
        <div className="drfq-header">
          <div className="drfq-header-title">Request Customs &amp; Delivery Services</div>
          <div className="drfq-header-sub">from {forwarder.company_name}</div>
          <button className="drfq-close" onClick={handleClose} aria-label="Close">×</button>
        </div>

        {/* Success state */}
        {(submitState === 'success_confirmed' || submitState === 'success_pending') && (
          <div className="drfq-success">
            {submitState === 'success_confirmed' ? (
              <>
                <div className="drfq-success-icon">✓</div>
                <div className="drfq-success-title">Request Sent</div>
                <div className="drfq-success-msg">
                  Your request has been sent to <strong>{forwarder.company_name}</strong>.
                  They&apos;ll respond to your email shortly.
                </div>
              </>
            ) : (
              <>
                <div className="drfq-success-icon">✉</div>
                <div className="drfq-success-title">Check Your Inbox</div>
                <div className="drfq-success-msg">
                  We sent a confirmation email to <strong>{email}</strong>.
                  Click the link to send your request to {forwarder.company_name}.
                </div>
              </>
            )}
            <button className="drfq-close-btn" onClick={handleClose}>Close</button>
          </div>
        )}

        {/* Form */}
        {submitState !== 'success_confirmed' && submitState !== 'success_pending' && (
          <form className="drfq-form" onSubmit={handleSubmit} noValidate>
            <div className="drfq-section-label">Shipment</div>
            <div className="drfq-ref">{blReference}</div>

            <div className="drfq-section-label" style={{ marginTop: 20 }}>Services Requested</div>
            <label className="drfq-checkbox-row">
              <input
                type="checkbox"
                checked={customsClearance}
                onChange={(e) => setCustomsClearance(e.target.checked)}
              />
              <span>Customs Clearance</span>
            </label>
            <label className="drfq-checkbox-row">
              <input
                type="checkbox"
                checked={localDelivery}
                onChange={(e) => setLocalDelivery(e.target.checked)}
              />
              <span>Local Delivery</span>
            </label>

            {localDelivery && (
              <div className="drfq-field">
                <label className="drfq-label" htmlFor="drfq-delivery-address">Delivery Address</label>
                <input
                  id="drfq-delivery-address"
                  className="drfq-input"
                  type="text"
                  placeholder="Street address, city, postal code"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                />
              </div>
            )}

            <div className="drfq-section-label" style={{ marginTop: 20 }}>Your Details</div>

            <div className="drfq-field">
              <label className="drfq-label" htmlFor="drfq-name">Your Name</label>
              <input
                id="drfq-name"
                className="drfq-input"
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {!userEmail && (
              <>
                <div className="drfq-field">
                  <label className="drfq-label" htmlFor="drfq-email">Your Email</label>
                  <input
                    id="drfq-email"
                    className="drfq-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="drfq-note">
                  We&apos;ll send a confirmation email before forwarding your request.
                </div>
              </>
            )}

            {userEmail && (
              <div className="drfq-note drfq-note--auth">
                Sending as <strong>{userEmail}</strong>
              </div>
            )}

            {submitState === 'error' && (
              <div className="drfq-error">{errorMsg}</div>
            )}

            <button
              className="drfq-submit"
              type="submit"
              disabled={submitState === 'submitting'}
            >
              {submitState === 'submitting' ? 'Sending…' : 'Send Request'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
